import { Auth } from 'aws-amplify';
import { type FunctionComponent } from 'react';
import React, { Suspense, useEffect, useState } from 'react';

import LoadingScreenOverlay from '../../components/LoadingScreenOverlay';
import { installationData } from '../../installationData';
import { formClasses, pageClasses } from '../../utils/BEM';
import { type CompleteNewPasswordFormData } from './CompleteNewPasswordForm';
import CompleteNewPasswordForm from './CompleteNewPasswordForm';
import { getCredentials, saveCredentials } from './CredentialStore';
import { type LoginFormData } from './LoginForm';
import LoginForm from './LoginForm';
import logo from './logo.svg';

import '../../App.sass';
import '../../Bootstrap.sass';

const AmplifyLoginScreen = React.lazy(() => import('./AmplifyLoginScreen'));

type LoginScreenProps = {};
const LoginScreen: FunctionComponent<LoginScreenProps> = () => {
    const [useAmplify, setUseAmplify] = useState<boolean>(false);
    const [completeNewPasswordData, setCompleteNewPasswordData] = useState<any>(false);
    const [formData, setFormData] = useState<CompleteNewPasswordFormData & LoginFormData>({
        username: '',
        password: '',
        resetPassword: '',
        rememberPassword: true,
    });
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [busy, setBusy] = useState<boolean>(false);
    const login = async () => {
        try {
            setBusy(true);
            // remember only the password a completed sign-in used, never the temporary one
            let signedInPassword: string | null = null;
            if (completeNewPasswordData) {
                await Auth.completeNewPassword(completeNewPasswordData, formData.resetPassword, {});
                signedInPassword = formData.resetPassword;
            } else {
                const signIn = await Auth.signIn(formData.username, formData.password);
                if (signIn.challengeName === 'NEW_PASSWORD_REQUIRED') {
                    setCompleteNewPasswordData(signIn);
                } else {
                    signedInPassword = formData.password;
                }
            }
            if (formData.rememberPassword && signedInPassword !== null) {
                await saveCredentials(formData.username, signedInPassword);
            }
        } catch (e: any) {
            console.error(e);
            setErrorMessage(e.message);
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const maybeCreds = await getCredentials();
            if (cancelled) {
                return;
            }
            if (maybeCreds) {
                try {
                    setBusy(true);
                    await Auth.signIn(maybeCreds.username, maybeCreds.password);
                } catch (e: any) {
                    console.error(e);
                    setErrorMessage(e.message);
                } finally {
                    setBusy(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    return (
        <form
            onSubmit={async (evt) => {
                evt.preventDefault();
                await login();
            }}
        >
            <div className="container mt-4">
                <div className="row justify-content-md-center">
                    <div className="col-md-6 col-lg-4">
                        <img alt={`${installationData.name} Logo`} className="mb-4 border-bottom pb-4" src={logo} />
                        {useAmplify ? (
                            <Suspense fallback={<LoadingScreenOverlay />}>
                                <AmplifyLoginScreen />
                            </Suspense>
                        ) : (
                            <>
                                <div {...formClasses('fieldlike')}>
                                    {errorMessage ? (
                                        <p className="text-danger">{errorMessage}</p>
                                    ) : (
                                        <p {...pageClasses('muted')}>
                                            {completeNewPasswordData
                                                ? "You have logged in with a temporary password. Please set a new, permanent one. Please choose a password that you don't use on other services."
                                                : `Welcome to ${installationData.name} e-flight system. Please use the form below to log in. If you don't have an account yet, please contact our dispatching.`}
                                        </p>
                                    )}
                                </div>
                                {completeNewPasswordData ? (
                                    <CompleteNewPasswordForm {...{ busy, formData, setFormData }} />
                                ) : (
                                    <>
                                        <LoginForm {...{ busy, formData, setFormData }} />
                                        <div {...formClasses('fieldlike', undefined, 'text-end')}>
                                            <button
                                                type="submit"
                                                {...pageClasses('anchor-like-button')}
                                                onClick={(evt) => {
                                                    evt.preventDefault();
                                                    setUseAmplify(true);
                                                }}
                                            >
                                                Forgot password?
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </form>
    );
};

export default LoginScreen;
